import { Stack } from '@/components/layout';
import { Button } from '@/components/ui/button';
import Text from '@/components/ui/text/text';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks/use-scaling';
import { colors } from '@/theme/colors';
import { memo } from 'react';

type SessionActionButtonProps = {
  onPress?: () => void;
  numberPlayersTracked?: number;
};

const SessionActionButtonMobile = memo(
  ({
    onPress,
    leftText,
    buttonText,
  }: Omit<SessionActionButtonProps, 'numberPlayersTracked'> & {
    leftText: string;
    buttonText: string;
  }) => {
    const { scale, isTablet } = useScaling();
    const styles = useStyles();

    return (
      <Stack paddingX={scale({ phone: 18, tablet: 0 })} marginBottom={scale(24)}>
        <Stack direction="row" align="center" justify="space-between" width="100%">
          <Text
            variant="Font Montserrat/Subtitle/Subtitle 2 Bold 12px"
            fontWeight={isTablet ? undefined : '800'}
            style={styles.trackingText}>
            {leftText}
          </Text>
          <Button style={styles.addButton} onPress={onPress}>
            <Button.Icon name="IcPlus" size={scale(24)} color={colors.Blue} />
            <Button.Text variant="Font Inter/Subtitle/Regular 12px">{buttonText}</Button.Text>
          </Button>
        </Stack>
      </Stack>
    );
  },
);

const SessionActionButtonTablet = memo(
  ({
    onPress,
    buttonText,
  }: Omit<SessionActionButtonProps, 'numberPlayersTracked'> & { buttonText: string }) => {
    const { scale } = useScaling();
    const styles = useStyles();

    return (
      <Stack direction="row" align="center" justify="flex-end">
        <Button style={styles.addButton} onPress={onPress}>
          <Button.Icon name="IcPlus" size={scale(24)} color={colors.Blue} />
          <Button.Text variant="Font Inter/Subtitle/Regular 12px">{buttonText}</Button.Text>
        </Button>
      </Stack>
    );
  },
);

export const AddTrackingButtonMobile = memo(
  ({ onPress, numberPlayersTracked }: SessionActionButtonProps) => {
    const leftText = STRINGS.playerSession.actions.trackingSummary(numberPlayersTracked ?? 0);
    return (
      <SessionActionButtonMobile
        onPress={onPress}
        leftText={leftText}
        buttonText={STRINGS.playerSession.actions.add}
      />
    );
  },
);

export const AddTrackingButtonTablet = memo(({ onPress }: SessionActionButtonProps) => {
  return (
    <SessionActionButtonTablet onPress={onPress} buttonText={STRINGS.playerSession.actions.add} />
  );
});

export const NewPlayerButtonMobile = memo(
  ({
    onPress,
    leftText,
  }: SessionActionButtonProps & {
    leftText?: string;
  }) => {
    return (
      <SessionActionButtonMobile
        onPress={onPress}
        leftText={leftText ?? STRINGS.playerSession.actions.selectedSummaryCount(0)}
        buttonText={STRINGS.playerSession.actions.new}
      />
    );
  },
);

export const NewPlayerButtonTablet = memo(({ onPress }: SessionActionButtonProps) => {
  return (
    <SessionActionButtonTablet onPress={onPress} buttonText={STRINGS.playerSession.actions.new} />
  );
});

const useStyles = createStyles(({ scale }) => ({
  trackingText: {
    color: colors.Blue,
  },
  addButton: {
    paddingVertical: scale(0),
    height: scale(40),
  },
}));
